import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IndianRupee,
  TrendingUp,
  AlertTriangle,
  BarChart3,
  PieChart,
  Filter
} from 'lucide-react';
import DashboardHeader from '../components/budget/DashboardHeader';
import FilterPanel from '../components/budget/FilterPanel';
import MetricsGrid from '../components/budget/MetricsGrid';
import BudgetUtilizationChart from '../components/budget/BudgetUtilizationChart';
import SpendingTrendsChart from '../components/budget/SpendingTrendsChart';
import CategoryBreakdownChart from '../components/budget/CategoryBreakdownChart';
import DepartmentComparisonChart from '../components/budget/DepartmentComparisonChart';

import { api } from '../utils/api';
import { useDebounce } from '../hooks/useDebounce';

function BudgetDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    dateRange: { start: null, end: null },
    departments: [],
    expenseGroups: [],
    expenseTypes: [],
    categories: []
  });

  // Dashboard data state
  const [dashboardData, setDashboardData] = useState({
    metrics: {
      totalBudget: 0,
      totalSpent: 0,
      remainingBudget: 0,
      utilizationRate: 0
    },
    departmentBreakdown: [],
    monthlyTrends: [],
    topCategories: null
  });

  // Debounce filters to prevent rapid API calls
  const debouncedFilters = useDebounce(filters, 500);

  // Load dashboard data when debounced filters change
  useEffect(() => {
    loadDashboardData();

    // Set up global refresh function for auto-refresh
    window.refreshBudgetData = loadDashboardData;

    return () => {
      // Cleanup global function
      delete window.refreshBudgetData;
    };
  }, [debouncedFilters]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await api.get('/api/budget/overview');

      const monthlyTrendsRaw = data.monthlyTrends || [];
      const monthlyTrends = {
        labels: monthlyTrendsRaw.map(m => {
          if (!m.month) return '';
          const [year, monthNum] = m.month.split('-');
          return new Date(year, monthNum - 1).toLocaleDateString('en-US', { month: 'short' });
        }),
        datasets: [
          {
            label: 'Total Spent',
            department: 'All Departments',
            data: monthlyTrendsRaw.map(m => m.totalSpent || 0),
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            borderColor: 'rgba(59, 130, 246, 1)',
            borderWidth: 2,
            fill: true
          }
        ]
      };

      const topCategoriesRaw = data.topCategories || [];
      const colors = ['rgba(59, 130, 246, 0.7)', 'rgba(16, 185, 129, 0.7)', 'rgba(245, 158, 11, 0.7)', 'rgba(239, 68, 68, 0.7)', 'rgba(168, 85, 247, 0.7)'];

      const topCategories = {
        level0: {
          title: 'Expense Categories',
          categories: topCategoriesRaw.map((cat, index) => ({
            name: cat.category,
            amount: cat.totalSpent || 0,
            color: colors[index % colors.length]
          }))
        }
      };

      setDashboardData({
        metrics: {
          totalBudget: data.totalBudget || 0,
          totalSpent: data.totalSpent || 0,
          remainingBudget: data.remainingBudget || 0,
          utilizationRate: data.utilizationRate || 0
        },
        departmentBreakdown: data.departmentBreakdown || [],
        monthlyTrends,
        topCategories,
        categoryBreakdown: []
      });

      setLoading(false);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load budget data. Please check your connection and try again.');
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleRefresh = () => {
    loadDashboardData();
  };

  if (error) {
    return (
      <div className="p-6 max-w-screen-xl mx-auto">
        <div
          className="rounded-xl p-6 border flex items-center gap-4"
          style={{
            background: 'var(--panel)',
            borderColor: '#ef4444',
            color: '#ef4444'
          }}
        >
          <AlertTriangle className="w-6 h-6" />
          <div>
            <h3 className="font-semibold">Error Loading Dashboard</h3>
            <p className="text-sm opacity-80">{error}</p>
            <button
              onClick={handleRefresh}
              className="mt-2 px-4 py-2 rounded-md text-sm font-medium"
              style={{
                background: 'var(--primary)',
                color: 'white'
              }}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg)' }}>
      {/* Dashboard Header */}
      <DashboardHeader
        onRefresh={handleRefresh}
        loading={loading}
        filters={filters}
        dashboardData={dashboardData}
      />

      <div className="p-6 w-full space-y-6">
        {/* Filter Panel */}
        <FilterPanel
          filters={filters}
          onFilterChange={handleFilterChange}
        />

        {/* Main Dashboard Content */}
        <div className="grid grid-cols-1 gap-6">


          {/* Metrics Grid */}
          <MetricsGrid
            metrics={dashboardData.metrics}
            loading={loading}
          />

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Budget Utilization Chart */}
            <div
              className="rounded-xl p-6 border"
              style={{
                background: 'var(--panel)',
                borderColor: 'var(--border)'
              }}
            >
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5" style={{ color: 'var(--primary)' }} />
                <h3 className="text-lg font-semibold" style={{ color: 'var(--text)' }}>
                  Budget Utilization
                </h3>
              </div>
              <BudgetUtilizationChart
                data={dashboardData.departmentBreakdown}
                loading={loading}
              />
            </div>

            {/* Category Breakdown Chart */}
            <div
              className="rounded-xl p-6 border"
              style={{
                background: 'var(--panel)',
                borderColor: 'var(--border)'
              }}
            >
              <div className="flex items-center gap-2 mb-4">
                <PieChart className="w-5 h-5" style={{ color: 'var(--primary)' }} />
                <h3 className="text-lg font-semibold" style={{ color: 'var(--text)' }}>
                  Category Breakdown
                </h3>
              </div>
              <CategoryBreakdownChart
                data={dashboardData.topCategories}
                loading={loading}
                onDrillDown={(category, index) => {
                  // Handle drill down to category
                }}
              />
            </div>
          </div>

          {/* Additional Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Spending Trends Chart */}
            <div
              className="rounded-xl p-6 border"
              style={{
                background: 'var(--panel)',
                borderColor: 'var(--border)'
              }}
            >
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5" style={{ color: 'var(--primary)' }} />
                <h3 className="text-lg font-semibold" style={{ color: 'var(--text)' }}>
                  Spending Trends
                </h3>
              </div>
              <SpendingTrendsChart
                data={dashboardData.monthlyTrends}
                loading={loading}
              />
            </div>

            {/* Department Comparison Chart */}
            <div
              className="rounded-xl p-6 border"
              style={{
                background: 'var(--panel)',
                borderColor: 'var(--border)'
              }}
            >
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5" style={{ color: 'var(--primary)' }} />
                <h3 className="text-lg font-semibold" style={{ color: 'var(--text)' }}>
                  Department Comparison
                </h3>
              </div>
              <DepartmentComparisonChart
                data={{
                  departments: dashboardData.departmentBreakdown.map(d => d.department),
                  metrics: {
                    budget: dashboardData.departmentBreakdown.map(d => d.budget),
                    spent: dashboardData.departmentBreakdown.map(d => d.spent),
                    remaining: dashboardData.departmentBreakdown.map(d => d.budget - d.spent),
                    utilization: dashboardData.departmentBreakdown.map(d => d.utilization)
                  }
                }}
                loading={loading}
              />
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default BudgetDashboard;